import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  WelcomeElement,
} from '@components';
import { GlobalContext } from '@contexts';
import { usePageTemplates } from '@hooks';
import { businessQuery, postsQuery, siteQuery } from '@queries';
import { SearchIcon } from '@svgs';

import PostRow from '../../Site/Blogs/PostRow/PostRow.jsx';
import { TemplateBreadcrumbs } from '../components/TemplateBreadcrumbs/TemplateBreadcrumbs.jsx';
import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

const matchPost = (post, query) => {
  const exactMatchFields = [post.id.toString()];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  const postFields = [post.slug, post.title];
  if (postFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)) {
    return true;
  }

  return false;
};

const filterPosts = (posts, query) => {
  const cleanedQuery = query.toLowerCase().trim();
  return posts.filter((post) => {
    if (matchPost(post, cleanedQuery)) {
      return post;
    }
  });
};

export const TemplateBlogs = () => {
  const { t } = useTranslation();
  const { businessID, siteID } = useParams();
  const globals = useContext(GlobalContext);

  const [searchTerm, setSearchTerm] = useState('');

  const { data: businessData } = useQuery(businessQuery(businessID));

  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  const {
    data: postsData,
    isPending: postsIsPending,
    isError,
  } = useQuery(
    postsQuery({
      limit: 5000,
      order_by: '-date',
      site_id: siteID,
      _fields: ['id', 'title', 'site_id', 'date', 'published'].join(','),
    }),
  );

  const { data: blogPages, isPending: blogPagesIsPending } = usePageTemplates(
    siteID,
    siteData?.theme_id,
    'blog',
  );

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  const filteredPosts = searchTerm
    ? filterPosts(postsData?.objects || [], searchTerm)
    : postsData?.objects;

  const noPosts =
    !postsIsPending && (isError || postsData?.objects?.length == 0);

  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <TemplateBreadcrumbs businessData={businessData} siteData={siteData}>
            <li className="sb-flex">
              <span>{t('Blog posts')}</span>
            </li>
          </TemplateBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>{t('Blog posts')}</h1>
              <div className="sb-button-list-inline">
                <Link
                  to={`/dashboard/projects/${businessID}/templates/${siteID}/blogs/posts/new`}
                  className="sb-btn sb-btn-cancel"
                >
                  Add new
                </Link>
              </div>
            </div>
            <div className="sb-manage-options-menu sb-flex">
              <div className="sb-manage-options-left">
                <div className="sb-flex">
                  <form
                    role="search"
                    className="sb-manage-options-search"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      name="query"
                      placeholder="Filter"
                      aria-label="Enter search term"
                      autoComplete="off"
                      defaultValue={searchTerm}
                      onChange={debouncedResults}
                      disabled={postsIsPending}
                    />
                    <SearchIcon />
                  </form>
                </div>
              </div>
              <div className="sb-manage-options-right">
                {/* <div className="sb-flex">
                  {globals.visible.siteBlogAuthorsView && (
                    <Link
                      to={`/dashboard/sites/${siteID}/blogs/authors`}
                      className="sb-btn sb-btn-cancel"
                    >
                      Manage authors
                    </Link>
                  )}
                  {globals.visible.siteBlogCategoriesView && (
                    <Link
                      to={`/dashboard/sites/${siteID}/blogs/categories`}
                      className="sb-btn sb-btn-cancel"
                    >
                      Manage categories
                    </Link>
                  )}
                </div> */}
              </div>
            </div>
            {globals.visible.projectTemplatesView &&
              !blogPagesIsPending &&
              blogPages.length == 0 && (
                <Message
                  messageStyle="warning"
                  title="No blog configured"
                  message="Blogging is currently not supported on this template. A blog page is required to support blogging."
                  fadeoutMessage={false}
                  isFormMessage={false}
                />
              )}
            {(postsIsPending || siteIsPending) && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {noPosts && (
              <WelcomeElement>
                <p>No blog posts under this template</p>
                <Link
                  to={`/dashboard/projects/${businessID}/templates/${siteID}/blogs/posts/new`}
                  className="sb-btn sb-btn-save"
                >
                  Add first post
                </Link>
              </WelcomeElement>
            )}
            {!postsIsPending && !isError && (
              <div className="sb-content-view-row">
                {filteredPosts.map((post) => {
                  return (
                    <PostRow
                      key={post.id}
                      post={post}
                      postUrl={`/dashboard/projects/${businessID}/templates/${siteID}/blogs/posts/${post.id}`}
                    />
                  );
                })}
              </div>
            )}
            {!postsIsPending && searchTerm && postsData && (
              <Message
                messageStyle="notice"
                title={
                  `Filtered to ${filteredPosts.length} of ${postsData.objects.length} post` +
                  (postsData.objects.length != 1 ? 's' : '')
                }
                fadeoutMessage={false}
                isFormMessage={false}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
